import CryptoJS from 'crypto-js';

/**
 * Service de cryptage pour sécuriser les données avant envoi à Firebase
 * Respecte les conventions de cryptage en front-end
 */
export class CryptoService {
  private static readonly SALT_ROUNDS = 12;
  private static readonly SECRET_KEY = process.env.REACT_APP_CRYPTO_SECRET || 'jup-default-secret-key';

  /**
   * Hache une chaîne avec SHA-256 et un sel
   */
  static hashString(data: string, salt?: string): string {
    const usedSalt = salt || CryptoJS.lib.WordArray.random(128/8).toString();
    const hash = CryptoJS.SHA256(data + usedSalt).toString();
    return `${hash}:${usedSalt}`;
  }

  /**
   * Vérifie un hash avec la donnée originale
   */
  static verifyHash(data: string, hashedData: string): boolean {
    try {
      const [hash, salt] = hashedData.split(':');
      const computedHash = CryptoJS.SHA256(data + salt).toString();
      return computedHash === hash;
    } catch (error) {
      console.error('Erreur lors de la vérification du hash:', error);
      return false;
    }
  }

  /**
   * Hache les données sensibles d'un utilisateur
   */
  static hashUserSensitiveData(userData: any): any {
    const hashedData = { ...userData };

    // Hash de l'adresse wallet (pour indexation sécurisée)
    if (hashedData.walletAddress) {
      hashedData.walletAddressHash = this.hashString(hashedData.walletAddress);
      // Garde seulement les 8 premiers et derniers caractères pour l'affichage
      hashedData.walletAddressDisplay = `${hashedData.walletAddress.slice(0, 8)}...${hashedData.walletAddress.slice(-8)}`;
    }

    // Hash de l'email si présent
    if (hashedData.email) {
      hashedData.emailHash = this.hashString(hashedData.email);
      delete hashedData.email; // Supprime l'email en clair
    }

    // Hash des liens sociaux sensibles
    if (hashedData.socialLinks) {
      const socialLinks = { ...hashedData.socialLinks };
      
      // Hash des identifiants sensibles mais garde les noms publics
      if (socialLinks.twitter && socialLinks.twitter.includes('@')) {
        socialLinks.twitterHash = this.hashString(socialLinks.twitter);
        socialLinks.twitter = socialLinks.twitter.split('@')[0]; // Garde juste le nom d'affichage
      }
      
      if (socialLinks.telegram && socialLinks.telegram.includes('@')) {
        socialLinks.telegramHash = this.hashString(socialLinks.telegram);
        socialLinks.telegram = socialLinks.telegram.split('@')[0];
      }

      hashedData.socialLinks = socialLinks;
    }

    // Hash des données de connexion
    if (hashedData.lastLoginIP) {
      hashedData.lastLoginIPHash = this.hashString(hashedData.lastLoginIP);
      delete hashedData.lastLoginIP;
    }

    return hashedData;
  }

  /**
   * Crypte des données avec AES
   */
  static encryptData(data: string): string {
    try {
      const encrypted = CryptoJS.AES.encrypt(data, this.SECRET_KEY).toString();
      return encrypted;
    } catch (error) {
      console.error('Erreur lors du cryptage:', error);
      throw new Error('Impossible de crypter les données');
    }
  }

  /**
   * Décrypte des données avec AES
   */
  static decryptData(encryptedData: string): string {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, this.SECRET_KEY);
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Erreur lors du décryptage:', error);
      throw new Error('Impossible de décrypter les données');
    }
  }

  /**
   * Hache un mot de passe avec PBKDF2
   */
  static hashPassword(password: string, salt?: string): string {
    const usedSalt = salt || CryptoJS.lib.WordArray.random(128/8).toString();
    const hash = CryptoJS.PBKDF2(password, usedSalt, {
      keySize: 256/32,
      iterations: 10000
    }).toString();
    return `${hash}:${usedSalt}`;
  }

  /**
   * Vérifie un mot de passe contre son hash
   */
  static verifyPassword(password: string, hashedPassword: string): boolean {
    try {
      const [hash, salt] = hashedPassword.split(':');
      const computedHash = CryptoJS.PBKDF2(password, salt, {
        keySize: 256/32,
        iterations: 10000
      }).toString();
      return computedHash === hash;
    } catch (error) {
      console.error('Erreur lors de la vérification du mot de passe:', error);
      return false;
    }
  }

  /**
   * Génère un token sécurisé
   */
  static generateSecureToken(length: number = 32): string {
    return CryptoJS.lib.WordArray.random(length).toString();
  }

  /**
   * Hash spécifique pour les messages sensibles
   */
  static hashMessage(content: string, authorId: string): string {
    const timestamp = Date.now().toString();
    const combined = `${content}:${authorId}:${timestamp}`;
    return CryptoJS.SHA256(combined).toString();
  }

  /**
   * Anonymise une adresse wallet pour les logs
   */
  static anonymizeWallet(walletAddress: string): string {
    if (!walletAddress || walletAddress.length < 16) return '***';
    return `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`;
  }

  /**
   * Hache les données d'un post avant sauvegarde
   */
  static hashPostData(postData: any): any {
    const hashedPost = { ...postData };

    // Hash du contenu si c'est un post privé ou sensible
    if (hashedPost.isPrivate && hashedPost.content) {
      hashedPost.contentHash = this.hashString(hashedPost.content);
    }

    // Hash des URLs de média pour vérifier l'intégrité
    if (hashedPost.mediaUrls && hashedPost.mediaUrls.length > 0) {
      hashedPost.mediaHashes = hashedPost.mediaUrls.map((url: string) => 
        this.hashString(url)
      );
    }

    return hashedPost;
  }

  /**
   * Hache les données sensibles des commentaires
   */
  static hashCommentData(commentData: any): any {
    const hashedComment = { ...commentData };

    // Hash du contenu pour détecter les modifications
    if (hashedComment.content) {
      hashedComment.contentHash = this.hashString(hashedComment.content);
    }

    return hashedComment;
  }
}

export default CryptoService;
